import React from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog,
  Box,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import useGenTasksAddHook from '../CustomHooks/useGenTasksAddHook';

import RepairAndMaintananceForm from './RepairAndmaintanance';
import GeneralTaskForm, { ALL_TASK_OPTIONS, GENERAL_TASKS } from './GeneralTaskForm';
import GenTaskDrivingForm from './GenTaskDrivingForm';
import ExpenseClaimForm from './GenTaskExpenseClaim';
import useGetUser from 'routes/Auth/useGetUser';

const REPAIR_TASK_TYPES = ['Repairs & Maintainance', 'External Repairs'];

// ── Component ──────────────────────────────────────────────────────────────────
const AddGenTaskFormDialog = ({
  open,
  setOpen,
  handleSuccessfulSubmit,
  vehicalList,
  trailerList,
  checkListOpt,
  typeOfRepairoption,
}) => {
  const {
    selectedTask,
    handleTaskChange,
    handleClose,
    submitNewTask,
    drivingData,
    setDrivingData,
    vehicalCheckList,
    EditVehicalCheckListData,
    generalFormData,
    setGeneralFormData,
    expenseClaimData,
    setExpenseClaimData,
    repairAndmaintananceData,
    setRepairAndmaintananceData,
    files,
    setFiles,
  } = useGenTasksAddHook(open, setOpen, handleSuccessfulSubmit);

  const { user } = useGetUser();

  const TASK_OPTIONS = user?.roleId === 1 ? ALL_TASK_OPTIONS : ALL_TASK_OPTIONS.filter(item => item !== 'Clock In');

  const handleFileChange = _fieldName => filesOrFile => {
    if (!filesOrFile) return;
    const selected = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    setFiles(prev => [...prev, ...selected]);
  };

  const handleDeleteFiles = file => {
    setFiles(prev => prev.filter(f => f !== file));
  };

  const normalisedFiles = files.map(f => ({ documentName: f.name, _rawFile: f }));

  const hasSubForm = !!selectedTask;

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': { overflow: 'hidden' },
      }}
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
      aria-labelledby="add-gentask-dialog-title"
      open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="add-gentask-dialog-title">
        Add New Task
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={theme => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon />
      </IconButton>

      <form
        onSubmit={e => {
          e.preventDefault();
          submitNewTask();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault();
        }}>
        <DialogContent
          dividers
          sx={{
            height: '75vh',
            overflow: 'auto',
            paddingBottom: '0.5rem',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', height: '100%', paddingBlock: '0.5rem' }}>
            {/* ── Task selector ── */}
            <TextField
              select
              required
              label="Select Task"
              value={selectedTask}
              onChange={e => handleTaskChange(e.target.value)}
              fullWidth
              size="small">
              {TASK_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>

            {/* ── Placeholder when no task chosen ── */}
            {!hasSubForm && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Select a task above to see the relevant form fields.
              </Typography>
            )}

            {/* ── Vehicle Check List ── */}
            {selectedTask === 'Vehicle Check List' && (
              <GenTaskDrivingForm
                data={drivingData}
                setData={setDrivingData}
                files={normalisedFiles}
                setUploadedFiles={setFiles}
                checklistOptions={checkListOpt}
                vehicalOption={vehicalList}
                trailerOptions={trailerList}
                checkListData={vehicalCheckList}
                handleCheckListEdit={EditVehicalCheckListData}
                isAdd
              />
            )}

            {/* ── General task types ── */}
            {GENERAL_TASKS.includes(selectedTask) && (
              <GeneralTaskForm data={generalFormData} setData={setGeneralFormData} isAdd />
            )}

            {/* ── Expense Claim ── */}
            {selectedTask === 'Expense Claim' && (
              <ExpenseClaimForm
                data={expenseClaimData}
                setData={setExpenseClaimData}
                files={normalisedFiles}
                isLoading={false}
                handleFileChange={handleFileChange}
                handleDeleteFiles={handleDeleteFiles}
                viewFile={null}
                isAdd
              />
            )}

            {/* ── Repairs & Maintenance / External Repairs ── */}
            {REPAIR_TASK_TYPES.includes(selectedTask) && (
              <RepairAndMaintananceForm
                data={repairAndmaintananceData}
                setData={setRepairAndmaintananceData}
                files={normalisedFiles}
                setUploadedFiles={setFiles}
                checklistOptions={checkListOpt}
                vehicalOption={vehicalList}
                trailerOptions={trailerList}
                checkListData={vehicalCheckList}
                handleCheckListEdit={EditVehicalCheckListData}
                typeOfRepairoption={typeOfRepairoption}
                selectedGenTask={null}
                isLoading={false}
                handleFileChange={handleFileChange}
                handleDeleteFiles={handleDeleteFiles}
                viewFile={null}
                isAdd
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit" disabled={!selectedTask}>
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddGenTaskFormDialog;
